import {
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
    Switch,
} from "@heroui/react";
import { useEffect, useRef, useState } from "react";
import { UnitDTO } from "shared/types/unit";
import { Flag } from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { applyMask } from "shared/utils/masks";
import { UnitIdLinkChip } from "../components/UnitIdLinkChip";

export interface UnitsInRenovationTableProps { 
    unitsInRenovation: UnitDTO[];
    fetchVacantUnits: () => Promise<void>;
    fetchUpcomingVacantUnits: () => Promise<void>;
    fetchUnitsInRenovation: () => Promise<void>;
}

const UnitsInRenovationTable = ({ unitsInRenovation, fetchVacantUnits, fetchUpcomingVacantUnits, fetchUnitsInRenovation }: UnitsInRenovationTableProps) => {
    const [units, setUnits] = useState<UnitDTO[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { session } = useSession();

    useEffect(() => {
        setUnits(unitsInRenovation);
        setIsLoading(false);
    }, [unitsInRenovation]);

    const handleUpdateUnit = async (unitId: string, updates: any) => {
        try {
            await fetchWithAuth(
                `${API_URL}/units/update/${unitId}`,
                {
                    method: "PUT",
                    body: JSON.stringify({ id: unitId, ...updates }),
                    headers: {
                        'Content-Type': 'application/json'
                    }
                },
                session
            );
            // If the renovation status changed, refresh all related tables
            if ('vacantDueToRenovation' in updates) {
                // Fetch data for all tables that share units
                fetchUnitsInRenovation();
                fetchVacantUnits();
                fetchUpcomingVacantUnits();
            } else {
                // Otherwise just refresh this table
                fetchUnitsInRenovation();
            }
        } catch (error) {
            console.error("Failed to update unit:", error);
        }
    };

    const calculateERVPerM2 = (unit: UnitDTO) => {
        if (!unit.estimatedRentalValue || !unit.netSurface) return "N/A";
        return `€ ${(unit.estimatedRentalValue / unit.netSurface).toFixed(2)}`;
    };

    // Inline date cell for renovation completion date
    const CompletionDateCell = ({ unit }: { unit: UnitDTO }) => {
        const [value, setValue] = useState(
            unit.renovationCompletionDate
                ? unit.renovationCompletionDate.toString().slice(0, 10)
                : ""
        );
        const prevValue = useRef(value);

        const save = async (newValue: string) => {
            if (newValue === prevValue.current) return;
            prevValue.current = newValue;
            const payload = { renovationCompletionDate: newValue || null };
            await handleUpdateUnit(unit.id as string, payload);
            setUnits(prev =>
                prev.map(u => u.id === unit.id ? { ...u, ...payload } : u)
            );
        };

        return (
            <input
                type="date"
                value={value}
                onChange={e => setValue(e.target.value)}
                onBlur={e => save(e.target.value)}
                className="px-2 py-1 text-sm border border-neutral-primary/20 rounded-sm focus:outline-none focus:border-moss-primary bg-transparent"
            />
        );
    };

    return (
        <div id="units-renovation" className="col-span-full flex flex-col gap-2">
            <header className="flex items-center justify-between">
                <div className="flex items-center gap-1 text-lg font-medium">
                    <Flag weight="duotone" size={20} />
                    Units in renovatie
                </div>
            </header>
            <div className="flex flex-col rounded-lg bg-white shadow-sm">
                {isLoading ? (
                    <div className="flex h-48 items-center justify-center">
                        <Spinner size="lg" label="Details laden..." />
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableColumn>Unit</TableColumn>
                            <TableColumn>In renovatie?</TableColumn>
                            <TableColumn>Opleveringsdatum</TableColumn>
                            <TableColumn>ERV</TableColumn>
                            <TableColumn>ERV/m²</TableColumn>
                            <TableColumn>Provisies</TableColumn>
                        </TableHeader>
                        <TableBody>
                            {units.map((unit) => (
                                <TableRow key={unit.id}>
                                    <TableCell>
                                        <UnitIdLinkChip
                                            unitId={unit.id as string}
                                            unitType={unit.unitType}
                                            unitSubtype={unit.unitSubtype}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Switch
                                            isSelected={unit.vacantDueToRenovation}
                                            onValueChange={(value) => {
                                                handleUpdateUnit(unit.id as string, {
                                                    vacantDueToRenovation: value,
                                                });
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <CompletionDateCell unit={unit} />
                                    </TableCell>
                                    <TableCell>{applyMask(unit.estimatedRentalValue, "valuta")}</TableCell>
                                    <TableCell>{calculateERVPerM2(unit)}</TableCell>
                                    <TableCell>{applyMask(unit.estimatedCommonCosts, "valuta")}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>
        </div>
    );
};

export default UnitsInRenovationTable; 